import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Grid,
    IconButton,
    Input,
    Select,
    Stack,
    Text,
    Textarea,
    Tooltip,
} from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";
import { FaCheck, FaTrashAlt } from "react-icons/fa";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import ListPageCardHeader from "components/Card/ListPageCardHeader";
import Loading from "components/Loading/Loading";
import { useCustomToast } from "components/CustomToast/CustomToast";
import { ROUTES } from "constants/routes-path";
import { CardType, maxParcels, minParcels, ViolationDiscount } from "constants/cards";
import { trafficViolationService } from "services/trafficViolationService";
import { violationCardPaymentsService } from "services/violationCardPaymentsService";
import { aitMask, checkErrors, hasError } from "utils/forms-utils";

function ViolationCardPaymentNew() {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const showToast = useCustomToast();

    const ids = useMemo(() => ({
        parcelas: "parcelas",
        tipoCartao: "tipo_cartao",
        observacoes: "observacoes",
        infracoes: {
            numeroAit: "numeroAit",
            desconto: "desconto"
        },
        resumoPagamento: {
            valorTotal: "valor_total",
            valorLiquido: "valor_liquido",
            valorParcela: "valor_parcela",
            valorJurosMes: "valor_juros_mes",
            valorJurosTotal: "valor_juros_total",
        }
    }), []);

    const cardSpacing = "20px";
    const formSpacing = "30px";
    const formDirection = { sm: "column", md: "row" };

    const [formData, setFormData] = useState();
    const [inputError, setInputError] = useState({});
    const [touched, setTouched] = useState({});
    const [loading, setLoading] = useState(true);
    const [violations, setViolations] = useState([]);
    const [validAit, setValidAit] = useState({});

    const initFormData = () => ({
        [ids.parcelas]: { value: "", validations: [{ required: true }, { min: minParcels }, { max: maxParcels }] },
        [ids.tipoCartao]: { value: "", validations: [{ required: true }] },
        [ids.observacoes]: { value: "", validations: [] },
    });

    const initViolation = () => ({
        [ids.infracoes.numeroAit]: "",
        [ids.infracoes.desconto]: "0",
    });

    function validateParcels(value) {
        const digitsOnly = value.replace(/\D/g, "");
        if (!digitsOnly) return "";

        const validatedValue = Math.max(minParcels, Math.min(maxParcels, parseInt(digitsOnly, 10)));
        return validatedValue.toString();
    }

    const toDTO = () => ({
        parcelas: formData[ids.parcelas].value,
        origem_pagamento: formData[ids.tipoCartao].value,
        observacoes: formData[ids.observacoes].value,
        infracoes: violations.map((v) => ({
            numero_ait: v[ids.infracoes.numeroAit],
            desconto: v[ids.infracoes.desconto],
        })),
    });

    const handleChange = (e) => {
        const { id, value } = e.target;
        let validatedValue = value;

        if (id === ids.parcelas) {
            validatedValue = validateParcels(value);
        }

        const newFormData = {
            ...formData,
            [id]: { ...formData[id], value: validatedValue },
        };

        const newErrors = checkErrors({ formData: newFormData, inputError, name: id });
        setFormData(newFormData);
        setInputError(newErrors);
    };

    const handleBlur = (e) => {
        const id = e.target.id;
        setTouched((prev) => ({ ...prev, [id]: true }));
    };

    const handleBlurAIT = async (index) => {
        const numeroAit = violations[index][ids.infracoes.numeroAit].trim();

        setTouched((prev) => ({
            ...prev,
            [`${ids.infracoes.numeroAit}_${index}`]: true,
        }));

        if (!numeroAit) { setValidAit((prev) => ({ ...prev, [index]: false })); return; }

        try {
            await trafficViolationService.findByAITNumber(numeroAit);
            setValidAit((prev) => ({ ...prev, [index]: true }));
        } catch (error) {
            const updated = [...violations];
            updated[index][ids.infracoes.numeroAit] = "";
            setViolations(updated);

            setValidAit((prev) => ({ ...prev, [index]: false }));
            showToast({
                title: t("traffic-violation.messages.errors.find"),
                status: "error",
            });
        }
    };

    const handleAddViolation = () => {
        setViolations((prev) => [...prev, initViolation()]);
    };

    const handleRemoveViolation = (indexToRemove) => {
        setViolations((prev) => prev.filter((_, index) => index !== indexToRemove));
    };

    const handleViolationChange = (index, field, value) => {
        const updated = [...violations];

        if (field === ids.infracoes.numeroAit) {
            updated[index][field] = aitMask(value.toUpperCase());
            setValidAit((prev) => ({
                ...prev,
                [index]: false,
            }));
        } else {
            updated[index][field] = value;
        }

        setViolations(updated);
    };

    const fetchPaymentData = async () => {
        if (!id) {
            setLoading(false);
            return;
        }

        try {
            const data = await violationCardPaymentsService.details(id);
            setFormData({
                [ids.parcelas]: { value: data.parcelas?.toString() || "", validations: [{ required: true }, { min: minParcels }, { max: maxParcels }] },
                [ids.tipoCartao]: { value: data.origem_pagamento || "", validations: [{ required: true }] },
                [ids.observacoes]: { value: data.observacoes || "", validations: [] },
                [ids.resumoPagamento.valorTotal]: data.valor_total,
                [ids.resumoPagamento.valorLiquido]: data.valor_liquido,
                [ids.resumoPagamento.valorParcela]: data.valor_parcela,
                [ids.resumoPagamento.valorJurosMes]: data.valor_juros_mes,
                [ids.resumoPagamento.valorJurosTotal]: data.valor_juros_total,
            });
        } catch {
            showToast({
                title: t("violation-card-payments.messages.errors.find"),
                status: "error",
            });
            redirectToList();
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        if (hasError({ formData, inputError })) return;

        try {
            const data = toDTO();

            if (id) {
                await violationCardPaymentsService.update(id, data);
            } else {
                await violationCardPaymentsService.create(data);
            }

            showToast({
                title: t("violation-card-payments.messages.success.create"),
                status: "success",
            });

            redirectToList();
        } catch {
            showToast({
                title: t("violation-card-payments.messages.errors.create"),
                status: "error",
            });
        }
    };

    const redirectToList = () => {
        navigate(ROUTES.violationCardPaymentsPath);
    };

    const handleRemove = async (violationCardPaymentId) => {
        try {
            await violationCardPaymentsService.remove(violationCardPaymentId);
            showToast({ title: t("violation-card-payments.messages.success.delete"), status: "success" });
            redirectToList();
        } catch (err) {
            showToast({ title: t(err), status: "error" });
        }
    };

    const handleFinalize = async (violationCardPaymentId) => {
        try {
            await violationCardPaymentsService.finalize(violationCardPaymentId);
            showToast({ title: t("violation-card-payments.messages.success.finalize"), status: "success" });
            redirectToList();
        } catch (err) {
            showToast({ title: t(err), status: "error" });
        }
    };

    useEffect(() => {
        setFormData(initFormData());
        fetchPaymentData();
    }, []);

    const isFormInvalid =
        !formData ||
        hasError({ formData, inputError }) ||
        (violations.length > 0 &&
            (!violations.every(
                (v) =>
                    v[ids.infracoes.numeroAit].trim() !== "" &&
                    v[ids.infracoes.desconto] !== ""
            ) ||
                !violations.every((_, index) => validAit[index] === true)));

    return (
        <Flex direction="column" minH="89vh" align="center" pt={{ sm: "120px", md: "75px" }}>
            <Card mt={"80px"}>

                {id && (
                    <Flex justify="flex-end" gap={3} mb={4}>
                        <Button
                            fontSize="sm"
                            colorScheme="red"
                            variant="outline"
                            onClick={() => handleRemove(id)}
                            leftIcon={<FaTrashAlt />}
                        >
                            {t("system.actions.remove")}
                        </Button>

                        <Button
                            fontSize="sm"
                            colorScheme="green"
                            variant="solid"
                            onClick={() => handleFinalize(id)}
                            leftIcon={<FaCheck />}
                        >
                            {t("violation-card-payments.list.actions.finish")}
                        </Button>
                    </Flex>
                )}

                <ListPageCardHeader
                    title={id ? t("violation-card-payments.edit-title") : t("violation-card-payments.new-title")}
                    subtitle={id ? `#${id}` : null}
                />
                <CardBody px="22px">
                    {loading || !formData ? (
                        <Loading h="300px" w="100%" />
                    ) : (
                        <Stack w="100%" direction="column" spacing={cardSpacing}>
                            <Stack spacing={formSpacing} direction={formDirection}>
                                <FormControl
                                    isRequired
                                    isInvalid={touched[ids.parcelas] && !!inputError[ids.parcelas]}
                                >
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t("violation-card-payments.form.fields.parcelas.label")}
                                    </FormLabel>
                                    <Input
                                        fontSize="sm"
                                        id={ids.parcelas}
                                        type="number"
                                        min={minParcels}
                                        max={maxParcels}
                                        value={formData[ids.parcelas].value}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        placeholder={t("violation-card-payments.form.fields.parcelas.placeholder")}
                                        autoFocus
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.parcelas])}
                                    </FormErrorMessage>
                                </FormControl>
                            </Stack>

                            <Stack spacing={formSpacing} direction={formDirection}>
                                <FormControl
                                    isRequired
                                    isInvalid={touched[ids.tipoCartao] && !!inputError[ids.tipoCartao]}
                                >
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t("violation-card-payments.form.fields.tipo-cartao.label")}
                                    </FormLabel>
                                    <Select
                                        fontSize="sm"
                                        id={ids.tipoCartao}
                                        value={formData[ids.tipoCartao].value}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                    >
                                        <option value=""></option>
                                        {Object.values(CardType).map((option, index) => (
                                            <option key={index} value={option.value}>
                                                {t(`violation-card-payments.form.fields.tipo-cartao.${option.labelKey}`)}
                                            </option>
                                        ))}
                                    </Select>
                                    <FormErrorMessage>
                                        {t(inputError[ids.tipoCartao])}
                                    </FormErrorMessage>
                                </FormControl>
                            </Stack>

                            <Stack spacing={formSpacing}>
                                <FormControl isInvalid={touched[ids.observacoes] && !!inputError[ids.observacoes]}>
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t("violation-card-payments.form.fields.observations.label")}
                                    </FormLabel>
                                    <Textarea
                                        fontSize="sm"
                                        id={ids.observacoes}
                                        value={formData[ids.observacoes].value}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        placeholder={t("violation-card-payments.form.fields.observations.placeholder")}
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.observacoes])}
                                    </FormErrorMessage>
                                </FormControl>
                            </Stack>

                            <Button
                                onClick={handleAddViolation}
                                colorScheme="blue"
                                size="sm"
                                width="150px"
                            >
                                + {t("violation-card-payments.form.actions.add-violation")}
                            </Button>

                            <Stack w="100%">
                                {violations.length > 0 && (
                                    <Grid
                                        templateColumns="1fr 1fr 80px"
                                        fontSize="xs"
                                        fontWeight="bold"
                                        alignItems="center"
                                    >
                                        <Text>{t("violation-card-payments.form.fields.ait-number.label")}</Text>
                                        <Text>{t("violation-card-payments.form.fields.discount.label")}</Text>
                                        <Text textAlign="center">{t("system.labels.actions")}</Text>
                                    </Grid>
                                )}

                                {violations.map((violation, index) => (
                                    <Grid key={index} templateColumns="1fr 1fr 80px" gap={2} alignItems="center">
                                        <FormControl
                                            isInvalid={
                                                touched[`${ids.infracoes.numeroAit}_${index}`] &&
                                                violation[ids.infracoes.numeroAit].trim() === ""
                                            }
                                        >
                                            <Input
                                                fontSize="sm"
                                                placeholder={t("violation-card-payments.form.fields.ait-number.placeholder")}
                                                value={violation[ids.infracoes.numeroAit]}
                                                onChange={(e) =>
                                                    handleViolationChange(index, ids.infracoes.numeroAit, e.target.value)
                                                }
                                                onBlur={() => handleBlurAIT(index)}
                                            />
                                        </FormControl>

                                        <FormControl>
                                            <Select
                                                fontSize="sm"
                                                value={violation[ids.infracoes.desconto]}
                                                onChange={(e) =>
                                                    handleViolationChange(index, ids.infracoes.desconto, e.target.value)
                                                }
                                            >
                                                {Object.values(ViolationDiscount).map((option, i) => (
                                                    <option key={i} value={option.value}>
                                                        {t(`violation-card-payments.form.fields.discount.${option.labelKey}`)}
                                                    </option>
                                                ))}
                                            </Select>
                                        </FormControl>

                                        <Flex justify="center">
                                            <Tooltip label={t("system.actions.remove")}>
                                                <IconButton
                                                    size="sm"
                                                    icon={<CloseIcon />}
                                                    colorScheme="red"
                                                    onClick={() => handleRemoveViolation(index)}
                                                    aria-label={t("system.actions.remove")}
                                                    w="40px"
                                                />
                                            </Tooltip>
                                        </Flex>
                                    </Grid>
                                ))}
                            </Stack>

                            {id && (
                                <Grid
                                    templateColumns={{ base: "1fr", md: "repeat(5, 1fr)" }}
                                    gap={6}
                                    fontSize="sm"
                                    fontWeight="bold"
                                >
                                    <Stack spacing={1} textAlign="center">
                                        <Text>{t("violation-card-payments.payment-values.total")}</Text>
                                        <Text>{formData[ids.resumoPagamento.valorTotal]}</Text>
                                    </Stack>

                                    <Stack spacing={1} textAlign="center">
                                        <Text>{t("violation-card-payments.payment-values.liquid")}</Text>
                                        <Text>{formData[ids.resumoPagamento.valorLiquido]}</Text>
                                    </Stack>

                                    <Stack spacing={1} textAlign="center">
                                        <Text>{t("violation-card-payments.payment-values.installment")}</Text>
                                        <Text>{formData[ids.resumoPagamento.valorParcela]}</Text>
                                    </Stack>

                                    <Stack spacing={1} textAlign="center">
                                        <Text>{t("violation-card-payments.payment-values.monthly-interest")}</Text>
                                        <Text>{formData[ids.resumoPagamento.valorJurosMes]}%</Text>
                                    </Stack>

                                    <Stack spacing={1} textAlign="center">
                                        <Text>{t("violation-card-payments.payment-values.total-interest")}</Text>
                                        <Text>{formData[ids.resumoPagamento.valorJurosTotal]}</Text>
                                    </Stack>
                                </Grid>
                            )}

                            <Flex w="100%" justify="space-between" mt="40px">
                                <Button
                                    variant="no-effects"
                                    alignSelf="flex-end"
                                    w="125px"
                                    bg="gray.100"
                                    color="gray.700"
                                    fontSize="sm"
                                    fontWeight="bold"
                                    onClick={redirectToList}
                                >
                                    {t("system.actions.cancel")}
                                </Button>
                                <Button
                                    w="100px"
                                    variant="dark"
                                    fontSize="sm"
                                    onClick={handleSubmit}
                                    _hover={{
                                        bg: "gray.500",
                                        color: "white",
                                    }}
                                    isDisabled={isFormInvalid}
                                >
                                    {t("system.actions.save")}
                                </Button>
                            </Flex>
                        </Stack>
                    )}
                </CardBody>
            </Card>
        </Flex>
    );
}

export default ViolationCardPaymentNew;